
import { ArticleDto } from "@/dtos/articleDto";
import { PriceDto } from "@/dtos/priceDto";
import { PriceListDto } from "@/dtos/priceListDto";
import { Component, Vue, Prop } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import PriceModelDefault, { PriceModel } from "@/dtos/priceModel";
import { WorkStepDto } from "@/dtos/workStepDto";

@Component
export default class PricelistButtonComponent extends Vue {
	@Prop()
	private article!: ArticleDto;
	@Prop()
	private subarticles!: ArticleDto[] | null;
	private priceModels = PriceModelDefault.allPriceModels;
	private isLoadingDetails: boolean = false;
	private isLoadingPrices: boolean = false;
	private showPriceDetailsDialog: boolean = false;
	private workSteps: WorkStepDto[] = [];
	private numberOfPartsShownInPriceDetails: number = 0;
	private openPanel: (number | null)[] = [];
	private workstepHeaders: any[] = [
		{
			text: "Name",
			sortable: false,
		},
		{
			text: "Gesamtdauer",
			sortable: false,
		},
		{
			text: "Preis / Stk",
			sortable: false,
		},
		{
			text: "Gesamtpreis",
			sortable: false,
		},
		{
			text: "Material*",
			sortable: false,
		},
		{
			text: "Fertigerpreis",
			sortable: false,
		},
	];

	private get isAssembly(): boolean {
		return this.article.isAssembly;
	}

	private get showPriceList(): boolean {
		let showPrice = true;
		if (this.isAssembly && this.subarticles) {
			if (
				this.article.calculating ||
				!this.subarticles ||
				this.subarticles.some((sa: ArticleDto) => sa.calculating) ||
				this.subarticles.some((sa: ArticleDto) => sa.state != 0 && sa.state != 1)
			) {
				showPrice = false;
			}
		} else {
			if (this.article.calculating || (this.article.state != 0 && this.article.state != 1)) {
				showPrice = false;
			}
		}
		return showPrice;
	}

	private get articleAndSubarticleIds(): number[] {
		let ids = [this.article.articleId];
		if (this.subarticles && this.subarticles.length > 0) {
			this.subarticles.forEach((s: ArticleDto) => ids.push(s.articleId));
		}
		return ids;
	}

	private get hasPrices() {
		return this.sortedPrices.length > 0;
	}

	private get sortedPrices() {
		if (this.article.priceLists.length <= 0) {
			return [];
		} else {
			let sortedPrices: PriceDto[] = Array.from(this.article.priceLists[0].prices);
			return sortedPrices.sort((a: any, b: any) => a.quantity - b.quantity);
		}
	}
	private get availablePriceModels() {
		let availablePriceModels = this.priceModels.filter((p: any) =>
			this.article.priceLists.some((pl: PriceListDto) => pl.priceModel == p.value)
		);
		return availablePriceModels;
	}

	private get workStepPriceModelGroups() {
		if (this.workSteps.length > 0) {
			let grouped = this.groupByKey(this.workSteps, "priceModel");
			return grouped;
		}
		return [];
	}
	private groupByKey(array: any, key: string) {
		return (array as []).reduce((hash, obj) => {
			if (obj[key] === undefined) return hash;
			return Object.assign(hash, { [obj[key]]: ((hash[obj[key]] || []) as any).concat(obj) });
		}, {});
	}
	private filteredGroup(group: any, articleId: number): any[] {
		return group.filter((g: any) => g.articleId === articleId);
	}

	// If 0 is returned, it means there is only 1 of these subarticles in the assembly
	private getAmountOfSubarticleInAssembly(articleId: number): number {
		const articleWorkstep = this.workSteps.filter(
			(ws: WorkStepDto) => ws.articleId === articleId
		)[0];
		if (!articleWorkstep) return 0;
		return articleWorkstep.quantity !== this.numberOfPartsShownInPriceDetails
			? articleWorkstep.quantity
			: 0;
	}

	private async getPrices() {
		if (this.article.priceLists != null && this.article.priceLists.length > 0) {
			return;
		}
		try {
			this.isLoadingPrices = true;
			const response = await httpClient().get(
				`Calculation/LoadPrices?articleId=${this.article.articleId}`
			);
			if (response.data.length <= 0) {
				this.$emit("priceListsUpdated", null);
			} else {
				this.$emit("priceListsUpdated", response.data);
			}
		} catch {
			console.log("Error while loading prices for article.");
			this.$store.commit("setSnackbarErrorText", "Prices could not be loaded.");
		} finally {
			this.isLoadingPrices = false;
		}
	}

	private async showPriceDetails(price: any) {
		this.showPriceDetailsDialog = true;
		try {
			this.isLoadingDetails = true;
			this.workSteps = [];
			this.numberOfPartsShownInPriceDetails = price.quantity;
			const response = await httpClient().get(
				`Calculation/GetWorkStepsForArticle?articleId=${this.article.articleId}&quantity=${price.quantity}`
			);
			this.workSteps = response.data;
		} catch {
			console.log("Update article failed.");
			this.$store.commit("setSnackbarErrorText", "Fehler beim Laden der Preisdetails.");
		} finally {
			this.isLoadingDetails = false;
		}
	}

	private getPriceTableHeaders() {
		let headers = [];
		headers.push({ text: "Stk.", sortable: false, align: "right" });
		if (this.availablePriceModels.length > 1) {
			this.availablePriceModels.forEach((pm: any) =>
				headers.push({ text: `${pm.name}`, sortable: false, align: "right" })
			);
		} else {
			headers.push({ text: "Preis / Stk.", sortable: false, align: "right" });
		}
		headers.push({ text: "", sortable: false, align: "left" });
		return headers;
	}
	private getModelPrice(quantity: number, model: PriceModel): string {
		let list = this.article.priceLists.filter((pl: PriceListDto) => pl.priceModel == model)[0];
		return list.prices.filter((p: PriceDto) => p.quantity === quantity)[0].price.toFixed(2);
	}
	private getPriceModelName(model: PriceModel) {
		return this.priceModels.filter((p: any) => model == p.value)[0].name;
	}
}
